import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import * as storeResourceActions from '@catcore/actions/current-resource';
import {RestHttpService} from '@catcore/services/rest-http.service';
import {
  FeatureCreateResultType, GuiZone, Project, ProjectFeatureCreateInfo,
  ProjectFeatureCreateResult,
} from '@catcore/types/api-v2';
import {GenericLinkedResource} from '@catcore/types/base';
import {FileDownloadInfo} from '@catcore/types/common';
import {extractDataFromProperty, FileBlobResponse} from '@catcore/utils';
import {CreateFeatureResultFactory} from '@catcore/utils/create-facture-result-factory';
import {GenericResourceApiService} from '@catfeatures/services/generic-resource-api.service';
import {ProjectFeature} from '@catmodules/models/project-feature/types';
import {ProjectUnit} from '@catmodules/models/project-units/types';
import {Store} from '@ngrx/store';
import {Restangular} from 'ngx-restangular';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/finally';
import 'rxjs/add/operator/first';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import {isArray, isNullOrUndefined} from 'util';
import {FeatureTypeService} from "@catcore/services/feature-type/feature-type.service";

/**
 * Service to manage all sorts of Project related data provisioning
 */
@Injectable()
export class ProjectService extends RestHttpService<Project> {

  /**
   * WebAPI v2 base endpoint
   * @type {string}
   */
  BASE_V2_API_ENDPOINT = 'projects';

  /**
   * WebAPI v2 base url
   * @type {string}
   */
  BASE_URL_V2 = `/api/${this.BASE_V2_API_ENDPOINT}`;

  /**
   * Endpoint for creating a new global project based on a multi file upload
   * @type {string}
   */
  UPLOAD_URL = `/api/${this.BASE_V2_API_ENDPOINT}/from_upload`;

  listSubject = new BehaviorSubject([]);
  list$ = this.listSubject.asObservable();

  constructor(private http: HttpClient,
              private restangular: Restangular,
              private genericResourceApiService: GenericResourceApiService,
              private store: Store<any>,
              private featureService: FeatureTypeService) {
    super('projects', restangular);
  }

  /**
   * Returns a observable which provides at the end all available projects
   *
   * The result is based on the WebAPI v2 models
   *
   * @returns {Observable<Project[]>}
   */
  getProjects(): Observable<Project[]> {
    return this
      .getAll()
      .map((projects: Project[]) => {
        this.listSubject.next(projects);
        return projects;
      })
      ;
  }

  /**
   * Trigger refreshing the current list of all projects
   *
   */
  refreshList(): void {
    this.getProjects().subscribe();
  }

  /**
   * Returns a observable project for a provided ID
   *
   * @param {string} projectId
   * @returns {Observable<Project>}
   */
  getProject(projectId: string): Observable<Project> {
    return this.getOne(projectId);
  }

  /**
   * Returns the as current active defined Project model
   *
   * @returns {Observable<Project>}
   */
  getCurrentProject(): Observable<Project> {
    return this.store.select((state) => {
      return state.core.currentProject.project;
    });
  }

  /**
   * Set the as current active to be seen Project model
   * @param {Project} project
   */
  setCurrentProject(project: Project): void {
    this.store.dispatch({
        type: storeResourceActions.CORE_CURRENT_PROJECT_UPDATE,
        payload: {project},
      },
    );
  }

  /**
   * Reset the current in use project
   * @param {Project} project
   */
  resetCurrentProject(): void {
    this.store.dispatch({
        type: storeResourceActions.CORE_CURRENT_PROJECT_UPDATE,
        payload: {project: null},
      },
    );
  }

  /**
   * Returns a list of ProjectUnit if they are available in the provided Project model
   *
   * @param {Project} project
   * @returns {ProjectUnit[]}
   */
  extractUnitsFrom(project: Project): ProjectUnit[] {
    return extractDataFromProperty(project, 'all_units', '_embedded');
  }

  /**
   * Returns a list of ProjectFeature if they are available in the provided Project model
   *
   * @param {Project} project
   * @returns {ProjectFeature[]}
   */
  extractFeaturesFrom(project: Project): ProjectFeature[] {
    return extractDataFromProperty(project, 'all_features', '_embedded');
  }

  /**
   * Returns a list of GuiZone if they are available in the provided Project model
   *
   * @param {Project} project
   * @returns {GuiZone[]}
   */
  extractGuiZonesFrom(project: Project): GuiZone[] {
    return extractDataFromProperty(project, 'gui_zones', '_embedded');
  }

  /**
   * Returns a list of units available in the provided Feature.
   *
   * @param project
   * @param projectFeature
   */
  extractUnitsForFeature(project: Project, projectFeature: ProjectFeature): ProjectUnit[] {
    const allUnits = this.extractUnitsFrom(project);
    const units: ProjectUnit[] = allUnits.filter((item: ProjectUnit) => {
      return item._links.feature.href === projectFeature._links.self.href;
    });
    return units;
  }

  /**
   * Initialize a request to create the Project configurations
   *
   * @param {Project} project
   * @param userId
   * @param serviceName
   * @param withServiceInstances
   * @returns {Observable<any>}
   */
  createConfigs(project: Project, userId: string = null, serviceName: string = null, withServiceInstances: boolean = true): Observable<any> {
    const url = `${this.BASE_URL_V2}/${project.id}/create_config`;
    const params = {};
    if (!isNullOrUndefined(userId)) {
      params['user_id'] = userId;
    }
    if (!isNullOrUndefined(serviceName)) {
      params['service_name'] = serviceName;
    }
    if (!isNullOrUndefined(withServiceInstances)) {
      params['with_service_instances'] = (withServiceInstances ? 'true' : 'false');
    }

    this.store.dispatch({
        type: storeResourceActions.CONFIG_CREATE_START,
      },
    );

    return this
      .http
      .get(url, {params: params})
      .map((projectInfo) => {

        this.store.dispatch({
            type: storeResourceActions.CONFIG_CREATE_COMPLETE,
          },
        );

        return projectInfo;
      });
  }

  /**
   * Initialize the request to retrieve the Project configuration files
   *
   * @param {Project} project
   * @param withCsdbStub
   * @returns {Observable<any>}
   */
  exportConfigs(project: Project, withCsdbStub: boolean = false): Observable<any> {
    const params = {
      withCsdbStub: (withCsdbStub ? 'true' : 'false')
    };
    const url = `${this.BASE_URL_V2}/${project.id}/export_config`;
    return this.fileDownload(url, params);
  }

  /**
   * Initialize the request to retrieve the Project patch field information as files
   *
   * @param {Project} project
   * @returns {Observable<FileDownloadInfo>}
   */
  exportPatchFields(project: Project): Observable<FileDownloadInfo> {
    const url = `${this.BASE_URL_V2}/${project.id}/export_patch_fields`;
    return this.fileDownload(url);
  }

  /**
   * Initialize the request to retrieve the Project patch field link information as files
   *
   * @param {Project} project
   * @returns {Observable<FileDownloadInfo>}
   */
  exportPatchFieldLinks(project: Project): Observable<FileDownloadInfo> {
    const url = `${this.BASE_URL_V2}/${project.id}/export_patch_field_links`;
    return this.fileDownload(url);
  }

  /**
   * Initialize the request to retrieve the service list as a file
   *
   * @param {Project} project
   * @returns {Observable<FileDownloadInfo>}
   */
  exportServiceList(project: Project): Observable<FileDownloadInfo> {
    const url = `${this.BASE_URL_V2}/${project.id}/export_service_instance_list`;
    return this.fileDownload(url);
  }

  /**
   * Returns a observable information for creating a block diagram of the current project
   *
   * @param {Project} project
   * @returns {Observable<any>}
   */
  getBlockDiagram(project: Project) {
    const url = `${this.BASE_URL_V2}/${project.id}/block-diagram`;
    return this.http.get(url).map((blockDiagram) => blockDiagram);
  }

  /**
   * Returns an observable with the test rack setups of the given project
   *
   * @param {Project} project
   * @returns {Observable<any>}
   */
  getTestRackSetups(project: Project) {
    return this.getAllSubresources(project.id, 'test-rack-setups');
  }

  /**
   * Returns an observable with all units of the given project
   *
   * @param {Project} project
   * @param configurableOnly
   * @returns {Observable<any>}
   */
  getAllUnits(project: Project, configurableOnly: boolean = false) {
    return this.getAllSubresources(project.id, 'units', {configurableOnly: configurableOnly});
  }

  /**
   * Returns an observable with all evid functions of the given project
   *
   * @param {Project} project
   * @param configurableOnly
   * @returns {Observable<any>}
   */
  getAllEvidFunctions(project: Project, configurableOnly: boolean = false) {
    return this.getAllSubresources(project.id, 'evid-functions', {configurableOnly: configurableOnly});
  }

  /**
   * Returns an observable with all ggw profiles of the given project.
   *
   * @param {Project} project
   * @returns {Observable<any>}
   */
  getGgwProfiles(project: Project) {
    return this.getAllSubresources(project.id, 'ggw-profiles');
  }

  /**
   * Initialize the request to create a new global project
   *
   * @param {FormData} formData
   * @returns {Observable<any>}
   */
  submitConfigFilesAsFormData(formData: FormData): Observable<any> {
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json');
    return this.http.post(this.UPLOAD_URL, formData, {headers});
  }

  /**
   * Assign a Feature to the GuiZone of a Project
   *
   * @param {ProjectFeatureCreateInfo} payload
   * @returns {Observable<any>}
   */
  createFeature(payload: ProjectFeatureCreateInfo): Observable<any> {
    const url = '/api/project-features/from_feature';
    const requestPayload = {
      featureTypeId: payload.feature.id,
      featureName: payload.featureName,
      guiZoneId: payload.guiZone.id,
      selections: payload.selections,
    };
    const headers = new HttpHeaders({'Content-Type': 'application/hal+json', 'Accept': 'application/hal+json'});
    return this.http
      .post(url, requestPayload, {headers})
      .map((result: any) => {
        this.featureService.refreshCurrent();
        return resolveCreateFeatureResult(result);
      });
  }

  /**
   * Preparing the request and handling the response for retrieve a file form the WebAPI and preparing it for a download in the browser
   * @param url
   * @param params
   * @returns {Observable<FileDownloadInfo>}
   */
  private fileDownload(url, params = {}): Observable<FileDownloadInfo> {
    return this.http
      .get(url, {responseType: 'blob', observe: 'response', params: params})
      .map((response: HttpResponse<Blob>) => {
        const fileResponse = new FileBlobResponse(response);
        return {blob: fileResponse.body, filename: fileResponse.filename};
      });
  }

  getProjectServiceFiles(project: Project) {
    const url = `${this.BASE_URL_V2}/${project.id}/service-files`;
    return this.http.get(url).map((serviceFiles) => serviceFiles);
  }

  editProjectServiceFileVersion(version: number, url: string) {
    return this.http.put(`${url}`, {version});
  }

  getMediaSources(project: Project) {
    const url = `${this.BASE_URL_V2}/${project.id}/media-sources`;
    return this.http.get(url).map((mediaSources) => mediaSources);
  }

  getUploadFiles(project: Project) {
    const url = `${this.BASE_URL_V2}/${project.id}/uploaded-files`;
    return this.http.get(url).map((userdataFiles) => userdataFiles);
  }

  getGgwParameterOptions(project: Project, parameterType: string = null) {
    const url = `${this.BASE_URL_V2}/${project.id}/ggw-parameter-options`;
    const params = parameterType ? {parameter_type: parameterType} : {};
    return this.http.get(url, {params: params}).map((parameterOptions) => parameterOptions);
  }

  /**
   * Returns an observable with the tokens of the given project
   *
   * @param {Project} project
   * @returns {Observable<any>}
   */
  getProjectTokens(project: Project) {
    return this.getRawSubresource(project.id, 'option-tokens');
  }

  searchParameters(project: Project, searchString: string) {
    const url = `${this.BASE_URL_V2}/${project.id}/parameter-search`;
    return this.http.get(url, {params: {search: window['encodeURIComponent'](searchString)}}).map((parameterOptions) => parameterOptions);
  }

  /**
   * Returns the default zone assignments for this project.
   *
   * @param project
   */
  getGgwDefaultZoneAssignments(project: Project) {
    return this.getRawSubresource(project.id, 'ggw-default-zone-assignment');
  }

  /**
   * Returns the explicit zone assignments (GgwDeviceZoneAssignment entities) for this project.
   *
   * @param project
   */
  getGgwDevZoneAssignments(project: Project) {
    return this.getAllSubresources(project.id, 'ggw-device-zone-assignments');
  }

  /**
   * Returns the final zone assignment per ggw device, regarding explicit assignments and assignments caused
   * by the selected zone strategy.
   *
   * @param project
   */
  getGgwDevZoneAssignment(project: Project) {
    return this.getRawSubresource(project.id, 'ggw-device-zone-assignment');
  }

  getAllGgwDevices(project: Project, guiVisibleOnly: boolean = false, includeIconPositions: boolean = false) {
    return this.getAllSubresources(project.id, 'ggw-devices' + (includeIconPositions ? '-with-icon-positions' : ''), {
      'gui_visible_only': guiVisibleOnly ? 'true' : 'false'
    });
  }

  getAllGuiZones(project: Project) {
    return this.getAllSubresources(project.id, 'gui-zones');
  }

  getAllEndpointGgwDevices(project: Project) {
    return this.getAllSubresources(project.id, 'ggw-devices', {
      'endpoints_only': 'true'
    });
  }

  getAllGgwProfilePolicies(project: Project) {
    return this.getAllSubresources(project.id, 'ggw-profile-policies');
  }

  getGgwProfileDefaultZonePolicies(project: Project) {
    return this.getRawSubresource(project.id, 'ggw-profile-default-policies', {type: 'zone'});
  }

  getGgwProfileDefaultDevicePolicies(project: Project) {
    return this.getRawSubresource(project.id, 'ggw-profile-default-policies', {type: 'device', by_profile: 'false'});
  }

  getGgwProfileZonePolicies(project: Project) {
    return this.getRawSubresource(project.id, 'ggw-profile-zone-policies');
  }

  getGgwFixedRoutes(project: Project) {
    return this.getAllSubresources(project.id, 'ggw-fixed-routes');
  }

  getGgwDefaultRoutes(project: Project) {
    return this.getRawSubresource(project.id, 'ggw-default-routes');
  }

  getGgwDynamicRouteTemplates(project: Project) {
    return this.getAllSubresources(project.id, 'ggw-dynamic-route-templates');
  }

  addGgwDynamicRoute(project: Project, param: { source: any; sink: any; removeDefault: boolean }) {
    return this.postSubresource(project.id, 'ggw-add-dynamic-route', param);
  }

  getGgwDynamicRoutes(project: Project) {
    return this.getAllSubresources(project.id, 'ggw-dynamic-routes');
  }

  getChecklist(project: Project) {
    return this.getRawSubresource(project.id, 'checklist');
  }

  getAllServices(project: Project, serviceModelName: string = null) {
    const params = (serviceModelName ? {service_model_name: serviceModelName} : {});
    return this.getAllSubresources(project.id, 'services', params);
  }

  updateAllFeatures(project: Project, featureTypeId: number = null) {
    return this.getRawSubresource(project.id, 'update-all-features', featureTypeId ? { type: featureTypeId } : {});
  }

  g7kUpdateToCatEvid(project: Project) {
    return this.getRawSubresource(project.id, 'g7k-update-to-cat-evid');
  }

  g7kDowngradeToManualEvid(project: Project) {
    return this.getRawSubresource(project.id, 'g7k-downgrade-to-manual-evid');
  }

  getGgwZoneStrategyOptions() {
    return this.http
      .get(`/api/ggw-zone-strategy-options`);
  }

  getGgwProfileStrategyOptions() {
    return this.http
      .get(`/api/ggw-profile-strategy-options`);
  }

  getGgwRouteStrategyOptions() {
    return this.http
      .get(`/api/ggw-route-strategy-options`);
  }

  getAllSpeakerPorts(project: any) {
    return this.getAllSubresources(project.id, 'speaker-ports');
  }

  getAllAvodCatalogs(project: Project) {
    return this.getAllSubresources(project.id, 'project-avod-catalogs');
  }

  getAllAppSelections(project: any) {
    return this.getRawSubresource(project.id, 'app-selections');
  }

  getCsdbConfigFiles(project: any) {
    return this.getRawSubresource(project.id, 'csdb-config-files');
  }

  updateAppTemplate(project: any, appTemplate: string) {
    const params = {
      app_template: appTemplate
    };
    return this.getRawSubresource(project.id, 'update-app-template', params);
  }
}

const resolveCreateFeatureResult = (requestResults: any): ProjectFeatureCreateResult => {
  const factory = new CreateFeatureResultFactory(requestResults);
  return factory.resolve();
};



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_master@2/app/Resources/Public/src/app/modules/core/services/project.service.ts